import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearConsent,
  getStoredConsent,
  sendConsentDecision,
  trackPageView,
} from './client';

const ConsentContext = createContext({
  consent: null,
  decided: false,
  acceptAnalytics: () => {},
  rejectAnalytics: () => {},
  resetConsent: () => {},
});

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [ready, setReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setConsent(getStoredConsent());
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready || !consent?.analytics) return undefined;
    trackPageView();
    return undefined;
  }, [ready, consent?.analytics, location.pathname, location.search]);

  const acceptAnalytics = useCallback(async () => {
    const next = await sendConsentDecision({ analyticsAllowed: true });
    setConsent(next);
    await trackPageView();
  }, []);

  const rejectAnalytics = useCallback(async () => {
    const next = await sendConsentDecision({ analyticsAllowed: false });
    setConsent(next);
  }, []);

  const resetConsent = useCallback(() => {
    clearConsent();
    setConsent(null);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      decided: Boolean(consent),
      acceptAnalytics,
      rejectAnalytics,
      resetConsent,
    }),
    [consent, acceptAnalytics, rejectAnalytics, resetConsent]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
